import React from "react";
import styled from "styled-components";
import {
  CellInfo,
  TableCellRenderer
} from "react-table";
import { toast } from "react-toastify";
import moment from "moment-timezone";

import { LikeToDo } from "src/library/components/LikeToDo";
import { Button } from "src/library/components/Button";
import { Text } from "src/library/components/Text";
import { Table } from "src/library/components/Tables";
import { ITableInstance } from "src/library/components/Tables/Table";
import { PageProps } from "src/pages/Router/routes";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import {
  useSubstitutionsApi,
  IProductSubstitution
} from "src/modules/substitutions/api/substitutions.api";
import {
  FilterParams,
  PaginatedParams
} from "src/core/shared/api";
import { productSubstitutionTableColumns } from "src/core/shared/components/tables/productSubstitutionTableColumns";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";

const defaultPagination: PaginatedParams<IProductSubstitution> = {
  page: 0,
  limit: 10,
  offset: 0,
  sort: [ [ "createdAt", "ASC" ] ]
};

export const ProductSubstitutionsPage: React.ReactFCWithChildren<PageProps<unknown>> = ({
  history, terminology, ...props
}) => {
  const defaultFilters = { status: "active" };
  const TableRef = React.useRef<ITableInstance<IProductSubstitution>>(null);
  const [ listSubstitutionsResponse, listSubstitutionsRequest ] = useSubstitutionsApi("LIST_SUBSTITUTIONS");
  const [ exportSubstitutionsResponse, exportSubstitutions ] = useSubstitutionsApi("EXPORT_SUBSTITUTIONS");
  const checkPermission = useCheckUserPermission();
  const canCreateSub = checkPermission("create-substitution");

  // handle export substitution response
  React.useEffect(() => {
    if (exportSubstitutionsResponse.error) {
      toast.error("Could not export substitutions");
    }

    if (exportSubstitutionsResponse.data) {
      toast.success(exportSubstitutionsResponse.data.message);
    }
  }, [ exportSubstitutionsResponse.error, exportSubstitutionsResponse.data ]);

  const onFetchData = React.useCallback((
    filters: FilterParams<IProductSubstitution>,
    pagination: PaginatedParams<IProductSubstitution>
  ) => {
    listSubstitutionsRequest({
      filters: {
        ...filters,
        endDate: { $gte: moment.utc().format("YYYY-MM-DD") }
      },
      pagination
    });
  }, [ listSubstitutionsRequest ]);

  const navigateToSubstitution = React.useCallback((row: CellInfo) => (
    history.push(`/admin/products/substitutions/${row.original.id}`)
  ), [ history ]);

  const tableColumns = React.useMemo(() => {
    const ViewButton: TableCellRenderer = row => (
      <Button onClick={() => navigateToSubstitution(row)}>
        View
      </Button>
    );

    return productSubstitutionTableColumns(ViewButton);
  }, [ navigateToSubstitution ]);

  return (
    <Layout
      history={history}
      terminology={terminology}
      {...props}
    >
      <MainContentWrapper
        fill
        Header={canCreateSub && (
          <LikeToDo
            copy="What would you like to do?"
            buttons={[
              <Button
                key="create"
                onClick={() => history.push("/admin/products/substitutions/create")}
              >
                Substitute a product
              </Button>,
              <Button
                key="export-substitutions"
                onClick={() => exportSubstitutions({ name: "Manual Substitution Export" })}
              >
                Export Substitutions
              </Button>
            ]}
          />
        )}
      >
        <TableWrapper>
          <Text
            fontSize="h3"
            weight="bold"
          >
            Product substitutions
          </Text>

          <Table
            tableId="global-substitutions"
            ref={TableRef}
            onFetchData={onFetchData}
            clearFilters={() => null}
            tableOf="Substitutions"
            totalRows={listSubstitutionsResponse.data?.result.length || 0}
            data={listSubstitutionsResponse.data?.result || []}
            error={listSubstitutionsResponse.error}
            loading={listSubstitutionsResponse.pending}
            mainAction={navigateToSubstitution}
            manual={false}
            columns={tableColumns}
            defaultFilters={defaultFilters}
            defaultPagination={defaultPagination}
            pages={listSubstitutionsResponse.data ? listSubstitutionsResponse.data.pages : 0}
          />
        </TableWrapper>
      </MainContentWrapper>
    </Layout>
  );
};

const TableWrapper = styled.div`
  margin-top: 4rem;
`;
